import React, { useContext, useEffect } from 'react';
import InputError from '@/Components/InputError.jsx';
import InputLabel from '@/Components/InputLabel.jsx';
import PrimaryButton from '@/Components/PrimaryButton.jsx';
import TextInput from '@/Components/TextInput.jsx';
import { Head, useForm } from '@inertiajs/react';
import NakedLayout from '@/Layouts/NakedLayout.jsx';
import { DataContext } from '@/Components/DataContext.jsx';
import InfoIconWithPopover from '@/Components/MoreInfoPopover.jsx';

const Checkbox = ({ id, name, checked, onChange }) => {
  return (
    <input
      type="checkbox"
      id={id}
      name={name}
      checked={checked}
      onChange={onChange}
      className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 shadow-sm focus:ring-indigo-500 dark:bg-gray-700 dark:checked:bg-indigo-600"
    />
  );
};

export default function Feedback({ showLayout = true, setIsFeedbackModalOpen }) {
  const { data: resumeData } = useContext(DataContext);

  const { data, setData, post, processing, errors, reset } = useForm({
    user_id: resumeData.user ? resumeData.user.id : null,
    want_response: true,
    email: resumeData.contactDetails.email || '',
    feedback: '',
    include_diagnostics: true,
    localstorage: localStorage.getItem('resumeData'),
  });

  useEffect(() => {
    return () => {
      reset('feedback');
    };
  }, []);

  const submit = (e) => {
    e.preventDefault();
    post(route('feedback.store'), {
      onSuccess: () => {
        setIsFeedbackModalOpen(false);
      },
    });
  };

  const formContent = (
    <>
      <Head title="Feedback" />

      {showLayout && (
        <>
          <h2 className="text-2xl text-center mb-5 font-bold text-gray-900 dark:text-gray-100">
            Feedback
          </h2>
          <hr className="mb-5 dark:border-gray-700" />
        </>
      )}
      <p className="mb-5 text-center text-gray-700 dark:text-gray-300">
        Running into an issue or have an idea?
        <br />
        We value your feedback. Please share your thoughts with us.
      </p>
      <div className="flex flex-col lg:flex-row mt-5">
        <div className="w-full">
          <form onSubmit={submit}>
            <div className="mt-4">
              <textarea
                id="feedback"
                name="feedback"
                rows={8}
                value={data.feedback}
                className="mt-1 block w-full border-gray-300 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 focus:border-indigo-500 dark:focus:border-indigo-600 focus:ring-indigo-500 dark:focus:ring-indigo-600 rounded-md shadow-sm"
                onChange={(e) => setData('feedback', e.target.value)}
                required
              />

              <InputError message={errors.feedback} className="mt-2" />
            </div>

            <div className="flex items-center justify-end mt-4">
              <PrimaryButton className="ms-4" disabled={processing}>
                Submit Feedback
              </PrimaryButton>
            </div>

            <div className="mt-4 flex items-center">
              <Checkbox
                id="want_response"
                name="want_response"
                checked={data.want_response}
                onChange={(e) => setData('want_response', e.target.checked)}
              />
              <InputLabel
                htmlFor="want_response"
                value="May we email you if necessary?"
                className="ml-2"
              />
              <InputError message={errors.want_response} className="mt-2" />
            </div>

            {data.want_response && (
              <div className="mt-4">
                <InputLabel htmlFor="email" value="Email Address" />

                <TextInput
                  id="email"
                  type="email"
                  name="email"
                  value={data.email}
                  className="mt-1 block w-full"
                  autoComplete="email"
                  onChange={(e) => setData('email', e.target.value)}
                />

                <InputError message={errors.email} className="mt-2" />
              </div>
            )}

            <div className="mt-4 flex items-center">
              <Checkbox
                id="include_diagnostics"
                name="include_diagnostics"
                checked={data.include_diagnostics}
                onChange={(e) => setData('include_diagnostics', e.target.checked)}
              />
              <InputLabel
                htmlFor="include_diagnostics"
                value="Include Diagnostics?"
                className="ml-2"
              />
              <InfoIconWithPopover
                title="Note"
                content={
                  <>
                    This includes your resume and other relevant user data. This will be necessary
                    for us to reproduce your issue and provide support.
                  </>
                }
              />
              <InputError message={errors.include_diagnostics} className="mt-2" />
            </div>
          </form>
        </div>
      </div>
    </>
  );

  return showLayout ? <NakedLayout>{formContent}</NakedLayout> : formContent;
}
